import { useContext, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import InfoIcon from '@mui/icons-material/Info'
import { Grid, Tooltip } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { addHours, format, parse, set } from 'date-fns'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Autocomplete } from 'src/components/Autocomplete'
import { BoxButtons } from 'src/components/BoxButton'
import { CheckboxComponent } from 'src/components/Checkbox'
import { DatePicker } from 'src/components/DatePicker'
import { ErrorText } from 'src/components/ErrorText'
import { Stepper } from 'src/components/Stepper'
import { TextFieldCapitalize } from 'src/components/TextFieldCapitalize'
import { TextFieldComponent } from 'src/components/TextFieldComponent'
import { TextFieldMask } from 'src/components/TextFieldMask'
import DataContext from 'src/contexts/DataContext'
import { emitNotificationEvent } from 'src/events'
import { useGetIbgeStates } from 'src/services/ibge'
import { composePath } from 'src/utils'
import * as Yup from 'yup'
import * as S from './styles'

import {
  useGetIssuingAgencies,
  useGetNationalities,
  useGetSexTypes,
  useGetStates,
} from 'src/services/backend'

const PersonalDataPage = () => {
  const navigate = useNavigate()

  const { registration, setRegistration } = useContext(DataContext)
  const currentStep = registration.getStep(registration, 'candidateData')

  const currentDate = new Date()

  const dateValidationMessage = `O candidato deve ter entre ${
    currentDate.getFullYear() -
    addHours(new Date(registration.minBirthdate!), 3).getUTCFullYear()
  } e ${
    currentDate.getFullYear() -
    addHours(new Date(registration.maxBirthdate!), 3).getUTCFullYear()
  } anos`

  const toasts = {
    errorGetStates: () =>
      emitNotificationEvent({
        id: 'errorGetStates',
        type: 'error',
        title: 'Ops!',
        message: 'Ocorreu um erro ao carregar as opções de "Estado"',
        isIndeterminate: true,
        textButton: 'Entendi',
      }),
    errorGetSexTypes: () =>
      emitNotificationEvent({
        id: 'errorGetSexTypes',
        type: 'error',
        title: 'Ops!',
        message: 'Ocorreu um erro ao carregar as opções de "Sexo"',
        isIndeterminate: true,
        textButton: 'Entendi',
      }),
  }

  const validationScheema = Yup.object().shape({
    name: Yup.string().trim().required('Nome é obrigatório'),
    socialName: Yup.string().nullable(),
    sex: Yup.object()
      .shape({ label: Yup.string() })
      .nullable()
      .required('Sexo é obrigatório'),
    birth_date: (() => {
      let birthDateSchema = Yup.date()
        .nullable()
        .required('Data de nascimento é obrigatória')
        .test(
          'is-date-after-today',
          'Data de nascimento não pode ser maior que a data atual',
          (value) => value < currentDate
        )
        .typeError('Informe uma data de nascimento válida')

      if (registration.minBirthdate && registration.maxBirthdate) {
        const maxBirthDateFormatted = set(
          addHours(new Date(registration.maxBirthdate!), 3),
          { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }
        )

        const minBirthDateFormatted = set(
          addHours(new Date(registration.minBirthdate!), 3),
          { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }
        )

        birthDateSchema = birthDateSchema
          .max(minBirthDateFormatted, dateValidationMessage)
          .min(maxBirthDateFormatted, dateValidationMessage)
      }

      return birthDateSchema
    })(),
    whatsapp: Yup.string()
      .required('Whatsapp é obrigatório')
      .test(
        'have-length-sufficient',
        'O celular deve conter pelo menos 8 dígitos.',
        (value) => value.replaceAll('_', '')?.length > 13
      ),
    whatsappConfirmation: Yup.string()
      .oneOf([Yup.ref('whatsapp'), ''], 'Confirmação de whatsapp incorreta')
      .required('Confirmação de whatsapp é obrigatória')
      .test(
        'have-length-sufficient',
        'O celular deve conter pelo menos 8 dígitos.',
        (value) => value.replaceAll('_', '')?.length > 13
      ),
    email: Yup.string()
      .trim()
      .email('Informe um e-mail válido')
      .required('E-mail é obrigatório'),
    emailConfirmation: Yup.string()
      .trim()
      .email('Informe um e-mail válido')
      .oneOf([Yup.ref('email'), ''], 'Confirmação de e-mail incorreta')
      .required('Confirmação de e-mail é obrigatória'),
    is_foreign: Yup.boolean(),
    nationality: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .required('Nacionalidade é obrigatória'),
    document: Yup.string()
      .required('Número do documento é obrigatório')
      .matches(/\d/, 'Documento precisa possuir no mínimo um número'),
    issue_entity: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .required('Órgão expedidor é obrigatório'),
    issue_state: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .when('is_foreign', {
        is: false,
        then: (schema) => schema.required('Estado emissor é obrigatório'),
      }),
    issue_date: Yup.date()
      .nullable()
      .required('Data de emissão é obrigatória')
      .test(
        'issue-date',
        'Data de emissão deve ser maior ou igual a data de nascimento.',
        (value, { parent }) => {
          const { birth_date } = parent
          if (!birth_date || !value) {
            return true
          }

          const formattedBirthDate = new Date(birth_date).setHours(0, 0, 0, 0)
          const formattedIssueDate = new Date(value).setHours(0, 0, 0, 0)

          return formattedIssueDate >= formattedBirthDate
        }
      )
      .max(currentDate, 'Data de emissão não pode ser superior á data atual')
      .typeError('Informe uma data de emissão válida'),
    motherFullName: Yup.string().trim().required('Campo obrigatório'),
    fatherFullName: Yup.string().trim(),
  })

  const defaultValues = {
    name: '',
    socialName: '',
    sex: null,
    birth_date: null,
    whatsapp: '',
    whatsappConfirmation: '',
    email: '',
    emailConfirmation: '',
    is_foreign: false,
    nationality: null,
    document: '',
    issue_entity: null,
    issue_state: null,
    issue_date: null,
    motherFullName: '',
    fatherFullName: '',
  }

  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    watch,
    trigger,
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationScheema),
  })

  const getSexTypes = useGetSexTypes()
  const [getIbgeStates, getIbgeStatesResult] = useGetIbgeStates()

  const candidateDataFormData = registration.getStep(
    registration,
    'candidateData'
  )?.formData

  const submitForm = (data: any) => {
    const dataFormatted = {
      ...data,
      birth_date: format(data.birth_date, 'yyyy-MM-dd'),
      issue_date: format(data.issue_date, 'yyyy-MM-dd'),
    }

    if (isFormValid()) {
      if (isDirty) {
        registration.setFormData(
          setRegistration,
          'candidateData',
          dataFormatted
        )
        registration.setDocument(setRegistration, data.document)
      }

      navigate(composePath('/endereco', `admissao/${registration.course}`))
    }
  }

  const isFormValid = () => isValid || !Object.keys(errors)?.length

  const states = useGetStates()
  const issuingAgencies = useGetIssuingAgencies()
  const nationalities = useGetNationalities()

  const setFormData = (formData: any) => {
    Object.keys(formData).forEach((field) => {
      //@ts-ignore
      setValue(field, formData[field])
    })

    reset(
      {},
      {
        keepErrors: true,
        keepDirty: false,
        keepDirtyValues: false,
        keepValues: true,
        keepDefaultValues: false,
        keepIsSubmitted: true,
        keepTouched: false,
        keepIsValid: true,
        keepSubmitCount: true,
      }
    ) // resetando alguns estados especificos do formulario
  }

  useEffect(() => {
    if (!registration.course)
      navigate(`/admissao/${localStorage.getItem('course')}`)

    if (!!currentStep?.formData) setFormData(currentStep.formData)

    getIbgeStates()
  }, [])

  useEffect(() => {
    if (getIbgeStatesResult?.error) toasts.errorGetStates()
  }, [getIbgeStatesResult?.error])

  useEffect(() => {
    if (getSexTypes?.error) toasts.errorGetSexTypes()
  }, [getSexTypes?.error])

  return (
    <S.ContainerFlex onSubmit={handleSubmit(submitForm)}>
      <S.Content>
        <Stepper
          currentStepName={currentStep?.name}
          label={currentStep?.title}
          course={registration.course}
        />
        <S.Title>Informe os dados do(a) candidato(a)</S.Title>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldCapitalize
                  {...field}
                  label={'Nome completo do(a) candidato(a)'}
                  fullWidth
                  error={!!fieldState.error}
                />
              )}
            />
            <ErrorText message={errors.name && errors.name.message} />
          </Grid>
          <Grid item sm={12} xs={12}>
            <Tooltip title="O nome social é a forma como pessoas transgêneras e não binárias escolhem ser chamadas. Para solicitar a alteração do nome nos registros, entre em contato com a secretaria da escola.">
              <S.Info>
                <InfoIcon fontSize="large" />
                <p>
                  O nome social é a forma como pessoas transgêneras e não
                  binárias escolhem ser chamadas. Para solicitar a alteração do
                  nome nos registros, entre em contato com a secretaria da
                  escola.
                </p>
              </S.Info>
            </Tooltip>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="sex"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  options={getSexTypes.data?.response || []}
                  getOptionLabel={(sex) => sex.name}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextFieldComponent
                      {...params}
                      label="Sexo"
                      fullWidth
                      error={!!fieldState.error}
                    />
                  )}
                />
              )}
            />
            <ErrorText message={errors.sex && errors.sex.message} />
          </Grid>
          <Grid item xs={12} md={6} sm={6}>
            <Controller
              name="birth_date"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  sx={{
                    width: '100%',
                  }}
                  onChange={(value) => {
                    field.onChange(value)
                    if (control._formValues.issue_date) trigger('issue_date')
                  }}
                  label="Data de nascimento"
                  inputFieldProps={{ error: !!fieldState.error }}
                  value={
                    typeof field.value === 'string'
                      ? parse(field.value, 'yyyy-MM-dd', new Date())
                      : field.value
                  }
                  maxDate={new Date()}
                />
              )}
            />
            <ErrorText
              message={errors.birth_date && errors.birth_date.message}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="whatsapp"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldMask
                  {...field}
                  mask="(99) 99999-9999"
                  label="WhatsApp do(a) candidato(a)"
                  onChangeMask={(value) => {
                    field.onChange(value)
                    if (control._formValues.whatsappConfirmation)
                      trigger('whatsappConfirmation')
                  }}
                  error={!!fieldState.error}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <S.WhatsApp />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <ErrorText message={errors.whatsapp && errors.whatsapp.message} />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="whatsappConfirmation"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldMask
                  {...field}
                  mask="(99) 99999-9999"
                  label="Confirme o WhatsApp"
                  onChangeMask={field.onChange}
                  error={!!fieldState.error && !!watch('whatsapp')}
                  onPasteMask={(e) => e.preventDefault()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <S.WhatsApp />
                      </InputAdornment>
                    ),
                  }}
                  disabled={!watch('whatsapp')}
                />
              )}
            />
            {!!watch('whatsapp') && (
              <ErrorText
                message={
                  errors.whatsappConfirmation &&
                  errors.whatsappConfirmation.message
                }
              />
            )}
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  {...field}
                  fullWidth
                  label="E-mail do(a) candidato(a)"
                  error={!!fieldState.error}
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <S.Email />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <ErrorText message={errors.email && errors.email.message} />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="emailConfirmation"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  {...field}
                  fullWidth
                  label="Confirme o e-mail"
                  error={!!fieldState.error && !!watch('email')}
                  autoComplete="new-password"
                  onPaste={(e) => e.preventDefault()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <S.Email />
                      </InputAdornment>
                    ),
                  }}
                  disabled={!watch('email')}
                />
              )}
            />
            {!!watch('email') && (
              <ErrorText
                message={
                  errors.emailConfirmation && errors.emailConfirmation.message
                }
              />
            )}
          </Grid>

          <Grid item sm={6} xs={12}>
            <Controller
              name="is_foreign"
              control={control}
              render={({ field }) => (
                <S.FormControl
                  control={<CheckboxComponent onChange={field.onChange} />}
                  label={
                    <S.Typograph>CANDIDATO(A) É ESTRANGEIRO(A)</S.Typograph>
                  }
                />
              )}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <Controller
              name="nationality"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  options={nationalities?.data?.response || []}
                  getOptionLabel={(nat) => nat.name}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextFieldComponent
                      {...params}
                      label="Nacionalidade"
                      required
                      fullWidth
                      error={!!fieldState.error}
                    />
                  )}
                />
              )}
            />
            <ErrorText
              message={errors.nationality && errors.nationality.message}
            />
          </Grid>

          <Grid container item xs={12} spacing={2}>
            <Grid item lg={3} xs={12}>
              <Controller
                name="document"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    fullWidth
                    label={`${
                      watch('is_foreign') ? 'RNE' : 'RG'
                    } do(a) candidato(a)`}
                    error={!!fieldState.error}
                  />
                )}
              />
              <ErrorText message={errors.document && errors.document.message} />
            </Grid>
            <Grid item lg={3} xs={12}>
              <Controller
                name="issue_entity"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    options={issuingAgencies?.data?.response || []}
                    getOptionLabel={(agency) => agency.acronym}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        label="Órgão expedidor"
                        fullWidth
                        error={!!fieldState.error}
                      />
                    )}
                  />
                )}
              />
              <ErrorText
                message={errors.issue_entity && errors.issue_entity.message}
              />
            </Grid>
            <Grid item lg={3} xs={12}>
              <Controller
                name="issue_state"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    options={states?.data?.response || []}
                    getOptionLabel={(state) => state.acronym}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        label="Estado emissor"
                        fullWidth
                        error={!!fieldState.error}
                      />
                    )}
                  />
                )}
              />
              <ErrorText
                message={errors.issue_state && errors.issue_state.message}
              />
            </Grid>
            <Grid item lg={3} xs={12}>
              <Controller
                name="issue_date"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    {...field}
                    sx={{
                      width: '100%',
                    }}
                    label="Data de emissão"
                    inputFieldProps={{ error: !!fieldState.error }}
                    value={
                      typeof field.value === 'string'
                        ? parse(field.value, 'yyyy-MM-dd', new Date())
                        : field.value
                    }
                  />
                )}
              />
              <ErrorText
                message={errors.issue_date && errors.issue_date.message}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Controller
                name="motherFullName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    fullWidth
                    required
                    label="Nome completo da mãe"
                    error={!!fieldState.error}
                  />
                )}
              />
              <ErrorText
                message={errors.motherFullName && errors.motherFullName.message}
              />
              <S.HelperText>
                Preencher com as informações que estão no RG{' '}
              </S.HelperText>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="fatherFullName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    fullWidth
                    label="Nome completo do pai"
                    error={!!fieldState.error}
                  />
                )}
              />

              <ErrorText
                message={errors.fatherFullName && errors.fatherFullName.message}
              />
              <S.HelperText>
                Preencher com as informações que estão no RG{' '}
              </S.HelperText>
            </Grid>
          </Grid>
        </Grid>

        <BoxButtons
          onClickPrevius={() =>
            navigate(
              composePath('/documentos', `admissao/${registration.course}`)
            )
          }
          isLoading={false}
          disableNextButton={false}
        />
      </S.Content>
    </S.ContainerFlex>
  )
}

export { PersonalDataPage }
